import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Button,
    TextField,
    Alert,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Slider,
    Card,
    CardContent,
    Stack,
    IconButton,
    Chip
} from '@mui/material';
import {
    Save as SaveIcon,
    Refresh as RefreshIcon,
    Preview as PreviewIcon,
    Print as PrintIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const PassbookTransactionSettings = () => {
    const { user } = useAuth();
    
    // State for settings
    const [settingName, setSettingName] = useState('');
    const [savedSettings, setSavedSettings] = useState([]);
    const [selectedSetting, setSelectedSetting] = useState('');
    
    // Transaction row configuration
    const [fontSize, setFontSize] = useState(12);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [rowHeight, setRowHeight] = useState(25);
    const [startingTop, setStartingTop] = useState(100);
    const [leftMargin, setLeftMargin] = useState(50);
    
    // Transaction field positions (columns in each row)
    const [fieldSettings, setFieldSettings] = useState({
        serialNumber: { left: 0, width: 100, visible: true, order: 1 },
        refillBookingNo: { left: 110, width: 150, visible: true, order: 2 },
        cashMemoNo: { left: 270, width: 130, visible: true, order: 3 },
        deliveryDate: { left: 410, width: 120, visible: true, order: 4 }
    });
    
    // Sample transaction data for preview
    const [sampleTransactions] = useState([
        { id: 1, serialNumber: '001', refillBookingNo: 'RB001234', cashMemoNo: 'CM001234', deliveryDate: '2024-01-15' },
        { id: 2, serialNumber: '002', refillBookingNo: 'RB001235', cashMemoNo: 'CM001235', deliveryDate: '2024-01-20' },
        { id: 3, serialNumber: '003', refillBookingNo: 'RB001236', cashMemoNo: 'CM001236', deliveryDate: '2024-01-25' },
        { id: 4, serialNumber: '004', refillBookingNo: 'RB001237', cashMemoNo: 'CM001237', deliveryDate: '2024-02-01' },
        { id: 5, serialNumber: '005', refillBookingNo: 'RB001238', cashMemoNo: 'CM001238', deliveryDate: '2024-02-05' },
        { id: 6, serialNumber: '006', refillBookingNo: 'RB001239', cashMemoNo: 'CM001239', deliveryDate: '2024-02-10' },
        { id: 7, serialNumber: '007', refillBookingNo: 'RB001240', cashMemoNo: 'CM001240', deliveryDate: '2024-02-15' },
        { id: 8, serialNumber: '008', refillBookingNo: 'RB001241', cashMemoNo: 'CM001241', deliveryDate: '2024-02-20' }
    ]);
    
    // State for UI
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [showRealTimePreview, setShowRealTimePreview] = useState(true);

    // All available transaction fields
    const transactionFields = [
        { key: 'serialNumber', label: 'Serial Number', width: 100 },
        { key: 'refillBookingNo', label: 'Refill Booking No', width: 150 },
        { key: 'cashMemoNo', label: 'Cash Memo No', width: 130 },
        { key: 'deliveryDate', label: 'Delivery Date', width: 120 }
    ];

    useEffect(() => {
        loadSavedSettings();
    }, []);

    // Function to reset field settings to defaults
    const resetFieldSettings = () => {
        const defaultSettings = {
            serialNumber: { left: 0, width: 100, visible: true, order: 1 },
            refillBookingNo: { left: 110, width: 150, visible: true, order: 2 },
            cashMemoNo: { left: 270, width: 130, visible: true, order: 3 },
            deliveryDate: { left: 410, width: 120, visible: true, order: 4 }
        };
        setFieldSettings(defaultSettings);
    };

    // Function to attempt repair of corrupted field settings
    const repairCorruptedFieldSettings = (corruptedSettings) => {
        const repairedSettings = {};
        
        Object.keys(corruptedSettings).forEach(key => {
            const field = corruptedSettings[key];
            
            // If it's an array (corrupted), try to extract meaningful data or use defaults
            if (Array.isArray(field)) {
                // Get default values for this field
                const defaultField = transactionFields.find(f => f.key === key);
                const defaultWidth = defaultField ? defaultField.width : 100;
                
                // Use default structure - we can't recover the original positions
                repairedSettings[key] = {
                    left: key === 'serialNumber' ? 0 : 
                          key === 'refillBookingNo' ? 110 :
                          key === 'cashMemoNo' ? 270 : 410,
                    width: defaultWidth,
                    visible: true,
                    order: key === 'serialNumber' ? 1 :
                           key === 'refillBookingNo' ? 2 :
                           key === 'cashMemoNo' ? 3 : 4
                };
            } else if (field && typeof field === 'object' && !Array.isArray(field)) {
                // It's already a valid object, keep it
                repairedSettings[key] = field;
            } else {
                // Fallback to defaults
                const defaultField = transactionFields.find(f => f.key === key);
                repairedSettings[key] = {
                    left: 0,
                    width: defaultField ? defaultField.width : 100,
                    visible: true,
                    order: 1
                };
            }
        });
        
        return repairedSettings;
    };

    const loadSavedSettings = async () => {
        try {
            // Load saved transaction settings from the API
            const response = await api.get('/api/transactionsettings/names');
            const data = response.data;
            // Ensure we always set an array
            if (Array.isArray(data)) {
                setSavedSettings(data);
            } else {
                setSavedSettings([]);
            }
        } catch (error) {
            // If API endpoint doesn't exist yet, initialize with empty array
            setSavedSettings([]);
            
            // Only show error if it's not a 404 (endpoint not found)
            if (error.response && error.response.status !== 404) {
                setError('Failed to load saved settings. Please try again later.');
            }
        }
    };

    const loadSettings = async (settingName) => {
        if (!settingName) return;
        
        try {
            const response = await api.get(`/api/transactionsettings/${settingName}`);
            const settings = response.data;
            
            if (settings) {
                setFontSize(settings.fontSize || 12);
                setRowsPerPage(settings.rowsPerPage || 10);
                setRowHeight(settings.rowHeight || 25);
                setStartingTop(settings.startingTop || 100);
                setLeftMargin(settings.leftMargin || 50);
                
                if (settings.fieldSettings) {
                    try {
                        let parsedFieldSettings;
                        
                        if (typeof settings.fieldSettings === 'string') {
                            // New format: Clean JSON string (after backend fix)
                            parsedFieldSettings = JSON.parse(settings.fieldSettings);
                        } else if (typeof settings.fieldSettings === 'object' && settings.fieldSettings !== null) {
                            // Legacy format: Handle existing corrupted data
                            // Check if it's corrupted (contains arrays)
                            const hasCorruption = Object.keys(settings.fieldSettings).some(key => 
                                Array.isArray(settings.fieldSettings[key])
                            );
                            
                            if (hasCorruption) {
                                parsedFieldSettings = repairCorruptedFieldSettings(settings.fieldSettings);
                            } else {
                                // It's a valid object, use it directly
                                parsedFieldSettings = settings.fieldSettings;
                            }
                        } else {
                            return; // Don't update if we can't understand the format
                        }
                        
                        // Validate that the parsed settings have the expected structure
                        const hasValidStructure = Object.keys(parsedFieldSettings).every(key => {
                            const field = parsedFieldSettings[key];
                            return field && 
                                   typeof field === 'object' && 
                                   typeof field.left === 'number' && 
                                   typeof field.width === 'number' && 
                                   typeof field.visible === 'boolean';
                        });
                        
                        if (hasValidStructure) {
                            setFieldSettings(parsedFieldSettings);
                        } else {
                            setError('Loaded settings have invalid structure. Please reset to defaults or save new settings.');
                        }
                    } catch (parseError) {
                        setError('Failed to parse saved field settings. Using current settings.');
                    }
                }
            }
        } catch (error) {
            // If API endpoint doesn't exist, show a more specific message
            if (error.response && error.response.status === 404) {
                setError('Transaction settings not found.');
            } else {
                setError('Failed to load settings: ' + (error.response?.data?.message || error.message));
            }
        }
    };

    const handleSaveSettings = async () => {
        if (!settingName.trim()) {
            setError('Please enter a setting name');
            return;
        }

        setLoading(true);
        try {
            const settingsData = {
                settingName: settingName.trim(),
                fontSize,
                rowsPerPage,
                rowHeight,
                startingTop,
                leftMargin,
                fieldSettings: JSON.stringify(fieldSettings),
                createdBy: user?.email || 'system'
            };

            await api.post('/api/transactionsettings', settingsData);
            
            setSuccess('Settings saved successfully!');
            loadSavedSettings();
            
            // Auto-select the newly saved setting
            setSelectedSetting(settingName.trim());
            
        } catch (error) {
            // Handle different error scenarios
            if (error.response && error.response.status === 404) {
                setError('Transaction settings API is not yet available. Please check with your administrator.');
            } else if (error.response && error.response.status === 405) {
                setError('Database migration required. Transaction settings cannot be saved until the database is updated. Preview functionality still works.');
            } else if (error.response && error.response.status === 501) {
                // Table doesn't exist - provide helpful instructions
                const responseData = error.response.data;
                setError(`Database Setup Required: ${responseData.message}${responseData.instructions ? '\n\nInstructions: ' + responseData.instructions : ''}`);
            } else if (error.response && error.response.status >= 500) {
                setError('Server error: The TransactionSettings table may not exist in the database. Please run database migrations first.');
            } else {
                setError('Failed to save settings: ' + (error.response?.data?.message || error.message));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleLoadSettings = (event) => {
        const settingName = event.target.value;
        setSelectedSetting(settingName);
        setSettingName(settingName);
        loadSettings(settingName);
    };

    const handleDeleteSettings = async () => {
        if (!selectedSetting) return;
        
        if (window.confirm(`Are you sure you want to delete "${selectedSetting}" settings?`)) {
            try {
                await api.delete(`/api/transactionsettings/${selectedSetting}`);
                setSuccess('Settings deleted successfully!');
                loadSavedSettings();
                setSelectedSetting('');
                setSettingName('');
            } catch (error) {
                setError('Failed to delete settings: ' + error.message);
            }
        }
    };

    const handleFieldChange = (fieldKey, property, value) => {
        setFieldSettings(prev => {
            // Ensure prev[fieldKey] exists and has the right structure
            if (!prev[fieldKey] || typeof prev[fieldKey] !== 'object') {
                const defaultField = transactionFields.find(f => f.key === fieldKey);
                prev[fieldKey] = {
                    left: 0,
                    width: defaultField ? defaultField.width : 100,
                    visible: true,
                    order: 1
                };
            }
            
            return {
                ...prev,
                [fieldKey]: {
                    ...prev[fieldKey],
                    [property]: value
                }
            };
        });
    };

    const generateTransactionPageHTML = () => {
        const visibleFields = transactionFields.filter(field => fieldSettings[field.key]?.visible);
        
        return `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Gas Passbook - Transaction Page</title>
                <style>
                    body {
                        font-family: Arial, sans-serif;
                        margin: 0;
                        padding: ${leftMargin}px;
                        font-size: ${fontSize}px;
                        position: relative;
                        width: 17in;
                        height: 14in;
                        min-height: 14in;
                    }
                    .transaction-row {
                        position: absolute;
                        width: 100%;
                        height: ${rowHeight}px;
                        line-height: ${rowHeight}px;
                    }
                    .field {
                        position: absolute;
                        overflow: hidden;
                        white-space: nowrap;
                    }
                    .header-row {
                        font-weight: bold;
                        background-color: #f0f0f0;
                    }
                    @page {
                        size: 17in 14in;
                        margin: 0;
                    }
                    @media print {
                        html, body { 
                            width: 17in !important;
                            height: 14in !important;
                            margin: 0 !important; 
                            padding: ${leftMargin}px !important;
                            background: none !important;
                            border: none !important;
                            overflow: hidden !important;
                        }
                        .header-row {
                            background-color: transparent !important;
                        }
                        * {
                            -webkit-print-color-adjust: exact !important;
                            color-adjust: exact !important;
                        }
                    }
                </style>
            </head>
            <body>
                <!-- Header Row -->
                <div class="transaction-row header-row" style="top: ${startingTop - rowHeight}px;">
                    ${visibleFields.map(field => `
                        <div class="field" style="left: ${fieldSettings[field.key].left}px; width: ${fieldSettings[field.key].width}px;">
                            ${field.label}
                        </div>
                    `).join('')}
                </div>
                
                <!-- Transaction Rows -->
                ${sampleTransactions.slice(0, rowsPerPage).map((transaction, index) => `
                    <div class="transaction-row" style="top: ${startingTop + (index * rowHeight)}px;">
                        ${visibleFields.map(field => `
                            <div class="field" style="left: ${fieldSettings[field.key].left}px; width: ${fieldSettings[field.key].width}px;">
                                ${formatTransactionValue(transaction, field.key)}
                            </div>
                        `).join('')}
                    </div>
                `).join('')}
            </body>
            </html>
        `;
    };

    const formatTransactionValue = (transaction, fieldKey) => {
        switch (fieldKey) {
            case 'deliveryDate':
                return new Date(transaction.deliveryDate).toLocaleDateString();
            case 'serialNumber':
                return transaction.serialNumber || '';
            case 'refillBookingNo':
                return transaction.refillBookingNo || '';
            case 'cashMemoNo':
                return transaction.cashMemoNo || '';
            default:
                return transaction[fieldKey] || '';
        }
    };

    const handlePreviewInNewWindow = () => {
        const previewContent = generateTransactionPageHTML();
        const previewWindow = window.open('', '_blank', 'width=1632,height=1344');
        previewWindow.document.write(previewContent);
        previewWindow.document.close();
    };

    const handlePrint = () => {
        const printContent = generateTransactionPageHTML();
        const printWindow = window.open('', '_blank', 'width=1632,height=1344');
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.focus();
        
        // Wait for content to load before printing
        setTimeout(() => {
            printWindow.print();
        }, 500);
    };

    return (
        <Container maxWidth="xl">
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Passbook Transaction Print Settings
                </Typography>
                <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                    Configure how transactions are printed on passbook pages with custom positioning and formatting
                </Typography>
                
                {savedSettings.length === 0 && !error && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <strong>Getting Started:</strong> Design your transaction layout using the settings below. You can preview your layout anytime, and save it as a template once the database setup is complete.
                    </Alert>
                )}
                
                {savedSettings.length > 0 && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        <strong>Full Functionality Enabled:</strong> All features are now available! Design transaction layouts, save settings templates, and manage your print configurations.
                    </Alert>
                )}
                
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        <div style={{ whiteSpace: 'pre-wrap' }}>{error}</div>
                    </Alert>
                )}
                
                {success && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                        {success}
                    </Alert>
                )}
            </Box>

            <Stack spacing={3}>
                {/* Settings Management */}
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Settings Management
                        </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="end">
                                <TextField
                                    label="Setting Name"
                                    value={settingName}
                                    onChange={(e) => setSettingName(e.target.value)}
                                    size="small"
                                    sx={{ minWidth: 200 }}
                                />
                                <FormControl size="small" sx={{ minWidth: 200 }}>
                                    <InputLabel>Load Saved Settings</InputLabel>
                                    <Select
                                        value={selectedSetting}
                                        label="Load Saved Settings"
                                        onChange={handleLoadSettings}
                                    >
                                        {Array.isArray(savedSettings) ? savedSettings.map((name) => (
                                            <MenuItem key={name} value={name}>
                                                {name}
                                            </MenuItem>
                                        )) : null}
                                    </Select>
                                </FormControl>
                                <Button
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    onClick={handleSaveSettings}
                                    disabled={loading}
                                >
                                    Save Settings
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<RefreshIcon />}
                                    onClick={loadSavedSettings}
                                >
                                    Refresh
                                </Button>
                                {selectedSetting && (
                                    <IconButton
                                        color="error"
                                        onClick={handleDeleteSettings}
                                        title="Delete Settings"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                )}
                            </Stack>
                        </CardContent>
                    </Card>

                {/* Main Content Area with Settings and Preview */}
                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} sx={{ minHeight: '600px' }}>
                    {/* Page Layout Settings */}
                    <Box sx={{ flex: 0.7, minWidth: 250 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Page Layout Settings
                                </Typography>
                            <Stack spacing={3}>
                                <Box>
                                    <Typography gutterBottom>Font Size: {fontSize}px</Typography>
                                    <Slider
                                        value={fontSize}
                                        onChange={(e, value) => setFontSize(value)}
                                        min={8}
                                        max={20}
                                        step={1}
                                        marks={[
                                            { value: 8, label: '8px' },
                                            { value: 12, label: '12px' },
                                            { value: 16, label: '16px' },
                                            { value: 20, label: '20px' }
                                        ]}
                                    />
                                </Box>
                                
                                <Box>
                                    <Typography gutterBottom>Rows Per Page: {rowsPerPage}</Typography>
                                    <Slider
                                        value={rowsPerPage}
                                        onChange={(e, value) => setRowsPerPage(value)}
                                        min={5}
                                        max={25}
                                        step={1}
                                        marks={[
                                            { value: 5, label: '5' },
                                            { value: 10, label: '10' },
                                            { value: 15, label: '15' },
                                            { value: 20, label: '20' },
                                            { value: 25, label: '25' }
                                        ]}
                                    />
                                </Box>
                                
                                <TextField
                                    label="Row Height (px)"
                                    type="number"
                                    value={rowHeight}
                                    onChange={(e) => setRowHeight(parseInt(e.target.value) || 25)}
                                    size="small"
                                />
                                
                                <TextField
                                    label="Starting Top Position (px)"
                                    type="number"
                                    value={startingTop}
                                    onChange={(e) => setStartingTop(parseInt(e.target.value) || 100)}
                                    size="small"
                                />
                                
                                <TextField
                                    label="Left Margin (px)"
                                    type="number"
                                    value={leftMargin}
                                    onChange={(e) => setLeftMargin(parseInt(e.target.value) || 50)}
                                    size="small"
                                />
                            </Stack>
                        </CardContent>
                    </Card>
                </Box>

                    {/* Column Positioning Section */}
                    <Box sx={{ flex: 0.8, minWidth: 280 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                    <Typography variant="h6">
                                        Column Positioning
                                    </Typography>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={resetFieldSettings}
                                    >
                                        Reset to Defaults
                                    </Button>
                                </Stack>
                                <Stack spacing={2} sx={{ maxHeight: '450px', overflowY: 'auto' }}>
                                    {transactionFields.map((field) => (
                                        <Box key={field.key} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                                <Chip 
                                                    label={field.label} 
                                                    color={fieldSettings[field.key]?.visible ? 'primary' : 'default'}
                                                    size="small"
                                                />
                                                <Button
                                                    size="small"
                                                    onClick={() => handleFieldChange(field.key, 'visible', !fieldSettings[field.key]?.visible)}
                                                >
                                                    {fieldSettings[field.key]?.visible ? 'Hide' : 'Show'}
                                                </Button>
                                            </Stack>
                                            {fieldSettings[field.key]?.visible && (
                                                <Stack direction="row" spacing={1}>
                                                    <TextField
                                                        label="Left (px)"
                                                        type="number"
                                                        value={fieldSettings[field.key]?.left || 0}
                                                        onChange={(e) => handleFieldChange(field.key, 'left', parseInt(e.target.value) || 0)}
                                                        size="small"
                                                        sx={{ width: 100 }}
                                                    />
                                                    <TextField
                                                        label="Width (px)"
                                                        type="number"
                                                        value={fieldSettings[field.key]?.width || field.width}
                                                        onChange={(e) => handleFieldChange(field.key, 'width', parseInt(e.target.value) || field.width)}
                                                        size="small"
                                                        sx={{ width: 100 }}
                                                    />
                                                </Stack>
                                            )}
                                        </Box>
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>
                    </Box>

                    {/* Real-time Preview Panel */}
                    {showRealTimePreview && (
                        <Box sx={{ flex: 2.2, minWidth: 500 }}>
                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <CardContent sx={{ p: 2, borderBottom: '1px solid #eee', backgroundColor: '#f8f9fa' }}>
                                    <Typography variant="h6" sx={{ textAlign: 'center', color: '#666', mb: 1 }}>
                                        <PreviewIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                        Real-time Preview
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: '#666', mb: 1 }}>
                                        Canvas Size: 17" Width × 14" Height (Design Area)
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: '#666', mb: 2 }}>
                                        Adjust settings to see real-time changes
                                    </Typography>
                                    
                                    {/* Preview Actions */}
                                    <Stack 
                                        direction={{ xs: 'column', sm: 'row' }} 
                                        spacing={1} 
                                        justifyContent="center"
                                        sx={{ gap: 1 }}
                                    >
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<PreviewIcon />}
                                            onClick={handlePreviewInNewWindow}
                                        >
                                            Preview New Window
                                        </Button>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<PrintIcon />}
                                            onClick={handlePrint}
                                        >
                                            Test Print
                                        </Button>
                                    </Stack>
                                </CardContent>
                                
                                <Box sx={{ 
                                    flex: 1,
                                    p: 2,
                                    backgroundColor: '#f8f9fa',
                                    overflow: 'auto',
                                    minHeight: 0,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'flex-start'
                                }}>
                                    <Box sx={{ 
                                        position: 'relative', 
                                        width: '680px', // 17 inches * 40px/inch = 680px
                                        height: '560px', // 14 inches * 40px/inch = 560px
                                        backgroundColor: 'white', 
                                        border: '2px dashed #007bff',
                                        borderRadius: '4px',
                                        fontFamily: 'Arial, sans-serif',
                                        fontSize: `${fontSize}px`,
                                        paddingLeft: `${leftMargin}px`,
                                        transform: 'scale(0.7)', // Scale down to fit better in sidebar
                                        transformOrigin: 'top center'
                                    }}>
                                        {/* Header Row */}
                                        <Box sx={{
                                            position: 'absolute',
                                            top: `${startingTop - rowHeight}px`,
                                            left: 0,
                                            width: '100%',
                                            height: `${rowHeight}px`,
                                            backgroundColor: '#f0f0f0',
                                            fontWeight: 'bold',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}>
                                            {transactionFields
                                                .filter(field => fieldSettings[field.key]?.visible)
                                                .map(field => (
                                                    <Box
                                                        key={`header-${field.key}`}
                                                        sx={{
                                                            position: 'absolute',
                                                            left: `${fieldSettings[field.key].left}px`,
                                                            width: `${fieldSettings[field.key].width}px`,
                                                            overflow: 'hidden',
                                                            whiteSpace: 'nowrap',
                                                            padding: '2px 4px',
                                                            border: '1px solid #ddd',
                                                            backgroundColor: 'rgba(0,123,255,0.1)'
                                                        }}
                                                    >
                                                        {field.label}
                                                    </Box>
                                                ))
                                            }
                                        </Box>

                                        {/* Transaction Rows */}
                                        {sampleTransactions.slice(0, rowsPerPage).map((transaction, index) => (
                                            <Box
                                                key={`row-${index}`}
                                                sx={{
                                                    position: 'absolute',
                                                    top: `${startingTop + (index * rowHeight)}px`,
                                                    left: 0,
                                                    width: '100%',
                                                    height: `${rowHeight}px`,
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                {transactionFields
                                                    .filter(field => fieldSettings[field.key]?.visible)
                                                    .map(field => (
                                                        <Box
                                                            key={`${field.key}-${index}`}
                                                            sx={{
                                                                position: 'absolute',
                                                                left: `${fieldSettings[field.key].left}px`,
                                                                width: `${fieldSettings[field.key].width}px`,
                                                                overflow: 'hidden',
                                                                whiteSpace: 'nowrap',
                                                                padding: '2px 4px',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.2s ease',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(0,123,255,0.1)',
                                                                    transform: 'scale(1.02)'
                                                                }
                                                            }}
                                                            title={`${field.label} - ${formatTransactionValue(transaction, field.key)}`}
                                                        >
                                                            {formatTransactionValue(transaction, field.key)}
                                                        </Box>
                                                    ))
                                                }
                                            </Box>
                                        ))}

                                        {/* Preview Guide */}
                                        <Box sx={{
                                            position: 'absolute',
                                            top: 10,
                                            left: 10,
                                            right: 10,
                                            textAlign: 'center',
                                            color: '#666',
                                            fontSize: '14px',
                                            fontStyle: 'italic'
                                        }}>
                                            PREVIEW MODE - Transaction Layout Positioning
                                        </Box>
                                    </Box>
                                </Box>
                            </Card>
                        </Box>
                    )}
                </Stack>
            </Stack>

        </Container>
    );
};

export default PassbookTransactionSettings;